import { closePopup, openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import { canOfferIncentivizedOAAddition } from 'src/replicant/chatbot/chatbot.getters';
import {
  incentivizedOAAdditionGemsReward,
  type IncentivizedOAOrigin,
} from 'src/replicant/chatbot/chatbot.ruleset';
import { user } from 'src/StateObserver';
import { isInLineMiniApp } from '@play-co/gcinstant';
import {
  canRequestOfficialAccountFriend,
  isSubscribedToOfficialAccount,
  requestAndClaimIncentivizedOAAddition,
} from './chatbot.api';
import {
  trackIncentivizedOAClose,
  trackIncentivizedOAOpen,
  type ConsentSurface,
} from '../launch/onboarding.analytics';

/**
 * Show the reward confirmation after a verified OA addition.
 *
 * @param origin Surface whose successful OA addition earned the reward.
 */
async function showIncentivizedOAReward(
  origin: IncentivizedOAOrigin,
): Promise<void> {
  // Map the OA surface onto the popup dialog origin taxonomy.
  const popupOrigin = origin === 'launch_sequence' ? 'launch' : 'postLevel';

  // Reuse the existing resubscribe reward chest with the treatment amount.
  await openPopupPromise('popupResubscribeReward', {
    amount: incentivizedOAAdditionGemsReward,
    origin: popupOrigin,
  });
}

/**
 * Check whether a new incentivized OA surface should render now.
 *
 * @returns Whether the user is eligible, capable, and currently unsubscribed.
 */
async function canShowIncentivizedOASurface(): Promise<boolean> {
  // Preserve the Mini App eligibility of the resolved experiment.
  if (!isInLineMiniApp()) return false;

  // Progression and the lifetime claim marker gate the reward-bearing flow.
  if (!canOfferIncentivizedOAAddition(user())) return false;

  // Only LIFF clients with the friendship API can display the required half-modal.
  if (!canRequestOfficialAccountFriend()) return false;

  // Stop all incentive prompts as soon as server-side friendship is verified.
  const isSubscribed = await isSubscribedToOfficialAccount({
    forceServerSide: true,
  });
  if (isSubscribed) return false;

  // The user may see the incentive surface.
  return true;
}

/**
 * Show the bubble composition and LINE half-modal at the eligible reward amount.
 *
 * @param origin Level 5 or recurring launch surface.
 */
export async function showIncentivizedOAHalfModal(
  origin: 'launch_sequence' | 'level_5',
): Promise<void> {
  // Skip ineligible, rewarded, unsupported, and subscribed users.
  const canShow = await canShowIncentivizedOASurface();
  if (!canShow) return;

  // Open the game-owned bubble before presenting the native prompt.
  const overlayClosed = openPopupPromise('popupIncentivizedOAOverlay', {
    amount: incentivizedOAAdditionGemsReward,
  });

  let allTimeViewCount: number;
  try {
    // Record the surface after requesting the game-owned overlay.
    allTimeViewCount = await trackIncentivizedOAOpen(
      origin,
      'incentive_half_modal',
    );
  } catch (error) {
    // Close the blocking overlay and abort when opening analytics fail.
    closePopup('popupIncentivizedOAOverlay');
    captureGenericError('trackIncentivizedOAOpen failed', error);
    return;
  }

  // Open LINE above the blocking bubble and claim only after server verification.
  const result = await requestAndClaimIncentivizedOAAddition();

  // Remove the game-owned layer after LINE completes or is dismissed.
  closePopup('popupIncentivizedOAOverlay');
  await overlayClosed;

  // Pair analytics with the rendered surface and verified result.
  trackIncentivizedOAClose(
    origin,
    'incentive_half_modal',
    result,
    allTimeViewCount,
  );

  // Only a fresh server grant displays the reward confirmation.
  if (result !== 'granted') return;

  // Confirm the freshly granted reward and refreshed balance.
  await showIncentivizedOAReward(origin);
}

/**
 * Show the persistent game-native reminder after the Level 15 win.
 */
export async function showIncentivizedOALevel15Reminder(): Promise<void> {
  // Skip rewarded, unsupported, and already subscribed users.
  const canShow = await canShowIncentivizedOASurface();
  if (!canShow) return;

  // Record the native reminder as its own conversion surface.
  const origin: ConsentSurface = 'level_15';
  const allTimeViewCount = await trackIncentivizedOAOpen(
    origin,
    'incentive_popup',
  );

  // The popup remains open when LINE is dismissed and closes only explicitly.
  const result = await openPopupPromise('popupIncentivizedOAReminder', {
    origin: 'postLevel',
    amount: incentivizedOAAdditionGemsReward,
  });

  // Pair the reminder close with its final user or claim outcome.
  trackIncentivizedOAClose(origin, 'incentive_popup', result, allTimeViewCount);

  // Manual close, ineligibility, and existing claims do not show reward UI.
  if (result !== 'granted') return;

  // Confirm the freshly granted 200 gems.
  await showIncentivizedOAReward(origin);
}
